import { ReactElement } from 'react';
import { Form, Button } from '@appsumo/dorado-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues } from 'react-hook-form';

import { STEP, ACCOUNT_CREATION_ERROR_MESSAGE } from '~/constants/checkout';
import { useCheckoutAPI, useReCaptcha } from '~/hooks/checkout';
import { useCart } from '~/lib/cart';
import useUser from '~/lib/user';
import { SignUpProps } from '~/types/checkout';

import { signUpSchema } from '~/schemas/email';
import { Loader } from '~/components/ui';

interface SignUpFormProps {
  email: string;
  password: string;
  confirmation: string;
}

export default function SignUp({
  setStep,
  isSubmittingAccount,
  setIsSubmittingAccount,
}: Readonly<SignUpProps>): ReactElement {
  const { setStepErrorMessage } = useCheckoutAPI();
  const { mutateItems } = useCart();
  const { signUp } = useUser();
  const { handleReCaptchaVerify } = useReCaptcha();

  function onChange(data: FieldValues, name: string, type: string) {
    if (type === 'change') {
      setStepErrorMessage(STEP.ACCOUNT, '');
    }
  }

  const onSubmit = async (data: SignUpFormProps): Promise<void> => {
    if (isSubmittingAccount) {
      setStepErrorMessage(STEP.ACCOUNT, ACCOUNT_CREATION_ERROR_MESSAGE);
      return;
    }

    setIsSubmittingAccount(true);
    setStepErrorMessage(STEP.ACCOUNT, '');

    const [token, recaptchaError] = await handleReCaptchaVerify();

    if (recaptchaError) {
      setStepErrorMessage(STEP.ACCOUNT, recaptchaError);
      setIsSubmittingAccount(false);
      return;
    }

    // This will return an error if there is no session, no need to check for the session again
    const { error } = await signUp(
      data.email,
      data.password,
      data.confirmation,
      token,
    );

    setIsSubmittingAccount(false);

    if (error) {
      setStepErrorMessage(STEP.ACCOUNT, error);
      return;
    }

    mutateItems();
    setStep(STEP.BILLING);
  };

  return (
    <div className="w-full">
      <Form
        onSubmit={onSubmit}
        resolver={yupResolver(signUpSchema)}
        autoClearErrors
        watch={onChange}
      >
        <div
          className="flex flex-col gap-y-2"
          data-testid="account-checkout-signup-form"
        >
          <Form.Input
            label="Email"
            name="email"
            placeholder="Email"
            // @ts-ignore -- TODO add this type to dorado-react
            type="email"
            data-testid="account-checkout-signup-email"
          />
          <div className="grid w-full md:grid-cols-2">
            <span className="md:mr-2">
              <Form.Input
                name="password"
                label="Password"
                // @ts-ignore -- TODO add this type to dorado-react
                type="password"
                placeholder="Password"
                data-testid="account-checkout-signup-password"
              />
            </span>
            <span className="md:ml-2">
              <Form.Input
                name="confirmation"
                label="Confirm password"
                // @ts-ignore -- TODO add this type to dorado-react
                type="password"
                placeholder="Confirm password"
                data-testid="account-checkout-signup-confirm-password"
              />
            </span>
          </div>
        </div>
        <div>
          <Button
            disabled={isSubmittingAccount}
            className="mt-6 flex w-full justify-center md:w-fit"
            type="submit"
            data-testid="account-checkout-signup-submit"
          >
            Continue to billing information
            {isSubmittingAccount && (
              <span className="ml-2">
                <Loader size="sm" />
              </span>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
}
